/**
 * What the line under *Activity* says on the phone's **Day** and **3 days**
 * (Aaron, Oct 27, note 1.1.1: "Remove the date and number of stops bar and
 * move them up as a subline below the Activity title"): the day line that had
 * a bar of its own between the week strip and the hours moves up into the page
 * bar's sub, where the Contacts list says its count — so the bar goes and the
 * hours get its room.
 *
 * - Day, one person: the date and the stops, *Thu, Oct 22 · 4 stops*; a day
 *   they do not work, *Sat, Oct 24 · Off* (the column's all-day chip says
 *   why).
 * - 3 days, one person: the three days and their total, *Oct 22 – 24 · 9
 *   stops* (*· Off* when none of the three is worked).
 * - Several people: the day (*Thu, Oct 22*) or the three (*Oct 22 – 24*) and
 *   nothing more — the columns say whose is whose, and a total across
 *   everybody's day is nobody's.
 *
 * **The driving total is not in it.** The sub shares its row with the view
 * button, the faces and **+**, which leave it 166 px on Day and 150 on 3 days
 * at 390; *Thu, Oct 22 · 4 stops · about 52 min driving* is 212 at 12 px, so
 * it could only ever be drawn cut off mid-word. The drive notes stay on every
 * stop, and List keeps the day's total under each heading.
 *
 * The forecast is not here either: it stays under each date in the strip.
 */
import { dayComma, monthDay, type CalendarAnswer, type CalendarItem } from '@rowted/shared'

const isStop = (item: CalendarItem) => item.kind === 'appointment' || item.kind === 'meeting'

/** *Oct 22 – 24*, or *Oct 30 – Nov 1* across a month's end (the strip's month button says the year). */
export function threeDayRange(days: readonly string[]): string {
  const first = days[0]!
  const last = days[days.length - 1]!
  if (first === last) return monthDay(first)
  return first.slice(0, 7) === last.slice(0, 7)
    ? `${monthDay(first)} – ${Number(last.slice(8))}`
    : `${monthDay(first)} – ${monthDay(last)}`
}

const stopsWords = (stops: number): string => `${stops} ${stops === 1 ? 'stop' : 'stops'}`

/** The days themselves: *Thu, Oct 22* on Day, *Oct 22 – 24* on 3 days. */
export const phoneDaysWords = (days: readonly string[]): string =>
  (days.length === 1 ? dayComma(days[0]!) : threeDayRange(days))

/** The sub for the days shown — one (Day) or three (3 days) — as the calendar answered them. */
export function phoneDaySub(answer: CalendarAnswer, days: readonly string[]): string {
  const when = phoneDaysWords(days)
  const person = answer.people.length === 1 ? answer.people[0]! : null
  if (!person) return when
  const shown = person.days.filter((day) => days.includes(day.date))
  const stops = shown.reduce((sum, day) => sum + day.items.filter(isStop).length, 0)
  if (stops === 0 && shown.length > 0 && shown.every((day) => !day.working)) return `${when} · Off`
  return `${when} · ${stopsWords(stops)}`
}
